import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UserSelector from '../../../components/admin/notifications/UserSelector';
import MessageComposer from '../../../components/admin/notifications/MessageComposer';
import ScheduleSelector from '../../../components/admin/notifications/ScheduleSelector';
import BroadcastPreview from '../../../components/admin/notifications/BroadcastPreview';
import BroadcastHistory from '../../../components/admin/notifications/BroadcastHistory';
import { RecipientFilter } from '../../../types/notification-admin';
import { NotificationPriority } from '../../../types/user';
import { NotificationBroadcastService } from '../../../services/notificationBroadcastService';
import { useAuth } from '../../../contexts/AuthContext';

const steps = ['Select Recipients', 'Compose Message', 'Schedule Delivery', 'Preview & Send'];

/**
 * NotificationBroadcast - Multi-step wizard for creating and sending broadcast messages
 *
 * Features:
 * - Step 1: Select recipients with advanced filtering
 * - Step 2: Compose message with priority settings
 * - Step 3: Schedule for immediate or future delivery
 * - Step 4: Preview and confirm before sending
 * - Broadcast history view
 */
const NotificationBroadcast: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [refreshHistory, setRefreshHistory] = useState(0);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);

  // Broadcast form state
  const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>({ allUsers: false });
  const [recipientCount, setRecipientCount] = useState(0);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<NotificationPriority>(NotificationPriority.NORMAL);
  const [actionLabel, setActionLabel] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [scheduledFor, setScheduledFor] = useState<Date | undefined>(undefined);

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0) {
      if (recipientCount === 0) {
        setAlert({ type: 'warning', message: 'Please select at least one recipient' });
        return;
      }
    } else if (activeStep === 1) {
      if (!title.trim()) {
        setAlert({ type: 'warning', message: 'Please enter a title' });
        return;
      }
      if (!message.trim()) {
        setAlert({ type: 'warning', message: 'Please enter a message' });
        return;
      }
      if (title.length > 100) {
        setAlert({ type: 'warning', message: 'Title is too long (max 100 characters)' });
        return;
      }
      if (message.length > 500) {
        setAlert({ type: 'warning', message: 'Message is too long (max 500 characters)' });
        return;
      }
    }

    setAlert(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSend = async () => {
    if (!authState.user) {
      setAlert({ type: 'error', message: 'You must be logged in to send broadcasts' });
      return;
    }

    setSending(true);
    setAlert(null);
    try {
      // Create broadcast
      const broadcast = await NotificationBroadcastService.createBroadcast(
        {
          title,
          message,
          priority,
          actionLabel: actionLabel.trim() || undefined,
          actionUrl: actionUrl.trim() || undefined,
          recipientFilter,
          scheduledFor,
        },
        authState.user.uid
      );

      // If immediate send, send now
      if (!scheduledFor) {
        await NotificationBroadcastService.sendBroadcast(broadcast.id);
        setAlert({
          type: 'success',
          message: `Broadcast sent successfully to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}!`
        });
      } else {
        setAlert({ type: 'success', message: 'Broadcast scheduled successfully!' });
      }

      // Reset form
      setRecipientFilter({ allUsers: false });
      setRecipientCount(0);
      setTitle('');
      setMessage('');
      setPriority(NotificationPriority.NORMAL);
      setActionLabel('');
      setActionUrl('');
      setScheduledFor(undefined);
      setActiveStep(0);
      setRefreshHistory(prev => prev + 1);
    } catch (error: any) {
      console.error('Error sending broadcast:', error);
      setAlert({ type: 'error', message: `Failed to send broadcast: ${error.message}` });
    } finally {
      setSending(false);
    }
  };

  const handleRecipientFilterChange = (filter: RecipientFilter) => {
    setRecipientFilter(filter);
  };

  // Update recipient count when filter changes
  React.useEffect(() => {
    const updateCount = async () => {
      try {
        const count = await NotificationBroadcastService.getRecipientCount(recipientFilter);
        setRecipientCount(count);
      } catch (error) {
        console.error('Error getting recipient count:', error);
        setRecipientCount(0);
      }
    };

    updateCount();
  }, [recipientFilter]);

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <UserSelector
            value={recipientFilter}
            onChange={handleRecipientFilterChange}
            disabled={sending}
          />
        );
      case 1:
        return (
          <MessageComposer
            title={title}
            message={message}
            priority={priority}
            actionLabel={actionLabel}
            actionUrl={actionUrl}
            onTitleChange={setTitle}
            onMessageChange={setMessage}
            onPriorityChange={setPriority}
            onActionLabelChange={setActionLabel}
            onActionUrlChange={setActionUrl}
            disabled={sending}
          />
        );
      case 2:
        return (
          <ScheduleSelector
            scheduledFor={scheduledFor}
            onScheduleChange={setScheduledFor}
            disabled={sending}
          />
        );
      case 3:
        return (
          <BroadcastPreview
            title={title}
            message={message}
            priority={priority}
            actionLabel={actionLabel}
            actionUrl={actionUrl}
            recipientFilter={recipientFilter}
            recipientCount={recipientCount}
            scheduledFor={scheduledFor}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/notifications')}
          sx={{ mb: 2 }}
        >
          Back to Notification Dashboard
        </Button>

        <Typography variant="h4" gutterBottom>
          Send Broadcast Notification
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Send notifications to multiple users at once. Use filters to target specific user groups
          or schedule broadcasts for future delivery.
        </Typography>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Wizard */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: 400, mb: 4 }}>
          {getStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        <Divider sx={{ mb: 3 }} />
        <Box display="flex" justifyContent="space-between">
          <Button
            disabled={activeStep === 0 || sending}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>

          <Box display="flex" gap={2}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={sending || recipientCount === 0}
                startIcon={scheduledFor ? <ScheduleIcon /> : <SendIcon />}
                size="large"
              >
                {sending
                  ? 'Sending...'
                  : scheduledFor
                  ? 'Schedule Broadcast'
                  : 'Send Broadcast'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Broadcast History */}
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Broadcast History
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          View past broadcasts and manage scheduled messages
        </Typography>

        <BroadcastHistory refreshTrigger={refreshHistory} />
      </Paper>
    </Container>
  );
};

export default NotificationBroadcast;
